import {createAction} from '@reduxjs/toolkit'
import {RsvpStatus} from '@wix/events-types'
import {
  BI_ORIGINS,
  ChangeRsvpModalMode,
  ExperimentNames,
  MembersModal,
  getEventId,
  getEventTitle,
  isFull,
  isNoResponseEnabled,
  isRsvp,
  isTicketed,
} from '@wix/wix-events-commons-statics'
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import _ from 'lodash'
import {callAPI, createActions} from '../../../../commons/actions/data-action-helper'
import {isEditor} from '../../../../commons/selectors/environment'
import {isDemoMode} from '../../../../commons/selectors/instance'
import {openDialogModal} from '../../../../commons/services/modal'
import {FormStep, RSVP_STEPS} from '../constants'
import {getCurrentMemberDetails, getCurrentMemberId} from '../selectors/current-member-details'
import {getEvent as getEventSelector, isDemoEvent} from '../selectors/event'
import {
  getMemberEventRsvpStatus,
  getMemberRsvpId,
  isMemberAbleToFinishRsvp,
  isMemberRsvpExists,
} from '../selectors/member-rsvp'
import {inRsvpForm} from '../selectors/navigation'
import {isRequestPending, isRequestPendingOld} from '../selectors/pending-requests'
import {hasAgreedWithPolicies, hasPolicies} from '../selectors/policies'
import {getCurrentStep, getRsvpDetails, isStepCompleted, isStepVisible, shouldNavigateToForm} from '../selectors/rsvp'
import {createAsyncAction} from '../services/redux-toolkit'
import {RsvpError} from '../types/errors'
import {RegFormData} from '../types/registration'
import {GetState, State, ThankYouMessageState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {extractFormData} from '../utils/api-data-mapper'
import {getEvent, getEventOld, getMembers, getMembersOld} from './event'
import {ensureLoginForMembersOnly, promptLogin, promptLoginOld} from './members'
import {
  navigateToChangeRsvp,
  navigateToChangeRsvpOld,
  navigateToForm,
  navigateToFormOld,
  navigateToThankYouMessage,
  navigateToThankYouMessageOld,
  navigateToTicketsPicker,
  navigateToTicketsPickerOld,
} from './navigation'
import {registrationButtonClicked} from './registration'

export const RESET_RSVP_ERROR_OLD = 'RESET_RSVP_ERROR_OLD'
export const SEND_RSVP_OLD = createActions('SEND_RSVP_OLD')
export const UPDATE_RSVP_OLD = createActions('UPDATE_RSVP_OLD')
export const UPDATE_RSVP_STATUS_OLD = createActions('UPDATE_RSVP_STATUS_OLD')
export const DELETE_RSVP_OLD = createActions('DELETE_RSVP_OLD')
export const GET_MEMBER_RSVP_OLD = createActions('GET_MEMBER_RSVP_OLD')
export const SKIP_GET_MEMBER_EVENT_RSVP = 'SKIP_GET_MEMBER_EVENT_RSVP'
export const EDIT_RSVP_STEP_OLD = 'EDIT_RSVP_STEP_OLD'
export const SET_RSVP_DETAILS_OLD = 'SET_RSVP_DETAILS_OLD'
export const NEXT_RSVP_STEP_OLD = 'NEXT_RSVP_STEP_OLD'
export const CHANGE_RESPONSE_OLD = 'CHANGE_RESPONSE_OLD'

export const SEND_RSVP = 'SEND_RSVP'

export const getMemberRsvpOld = () => async (dispatch: Function, getState: GetState) => {
  const state = getState()

  const currentMemberId = getCurrentMemberId(state)
  const event = getEventSelector(state)

  if (currentMemberId && !isDemoEvent(state) && isRsvp(event)) {
    await dispatch(callAPI(GET_MEMBER_RSVP_OLD, getEventId(event), currentMemberId))

    if (isMemberRsvpExists(getState()) && inRsvpForm(getState())) {
      dispatch(navigateToChangeRsvpOld())
    }

    return
  }

  return dispatch({type: SKIP_GET_MEMBER_EVENT_RSVP})
}

export const getMemberRsvp = createAsyncAction<wix.events.rsvp.Rsvp | void, void>(
  'GET_MEMBER_RSVP',
  async (_params, {getState, dispatch, extra: {serverApi}}) => {
    const state = getState()

    const currentMemberId = getCurrentMemberId(state)
    const event = getEventSelector(state)

    if (currentMemberId && !isDemoEvent(state) && isRsvp(event)) {
      const response = await serverApi.getMemberRsvp(getEventId(event), currentMemberId)

      if (isMemberRsvpExists(getState()) && inRsvpForm(getState())) {
        dispatch(navigateToChangeRsvp())
      }

      return response
    }
  },
)

export const resetRsvpErrorOld = () => ({type: RESET_RSVP_ERROR_OLD})

export const resetRsvpError = createAction<void>('RESET_RSVP_ERROR')

export const sendRsvpOld = (eventId: string, guest: RegFormData) => async (dispatch: Function, getState: GetState) => {
  const state = getState()

  if (isRequestPendingOld(state, SEND_RSVP_OLD.REQUEST)) {
    return
  }

  const guestResponse = guest.response

  if (isDemoEvent(state) || isDemoMode(state.instance)) {
    let thankYouMessageState: ThankYouMessageState = guestResponse === RsvpStatus.YES ? 'yes' : 'no'

    if (guestResponse === RsvpStatus.WAITING) {
      thankYouMessageState = 'waiting'
    }

    return dispatch(navigateToThankYouMessageOld(thankYouMessageState))
  }

  const promise = dispatch(
    callAPI(SEND_RSVP_OLD, eventId, extractFormData(guest), guestResponse, getCurrentMemberDetails(state)?.id),
  )

  promise.catch((e: RsvpError) => {
    if (e.response?.data?.metadata?.member_already_registered) {
      dispatch(promptLoginOld())
    } else {
      throw e
    }
  })

  const response = await promise

  if (!_.isEmpty(response.rsvp)) {
    dispatch(navigateToThankYouMessageOld(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
  }

  return response
}

export const sendRsvp = createAsyncAction<wix.events.rsvp.CreateRsvpResponse, {eventId: string; guest: RegFormData}>(
  SEND_RSVP,
  async ({eventId, guest}, {dispatch, getState, extra: {serverApi}}) => {
    const guestResponse = guest.response
    const currentMemberId = getCurrentMemberDetails(getState())?.id

    const response = await serverApi
      .createRsvp(eventId, extractFormData(guest) as unknown as RegFormData, guestResponse, currentMemberId)
      .catch((e: RsvpError) => {
        if (e.response?.data?.metadata?.member_already_registered) {
          dispatch(promptLogin())
        }

        throw e
      })

    if (!_.isEmpty(response.rsvp)) {
      dispatch(navigateToThankYouMessage(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
    }

    return response
  },
  {
    condition: (_params, {getState}) => {
      const state = getState()

      return !isRequestPending(state, SEND_RSVP)
    },
  },
)

export const updateRsvpOld =
  (eventId: string, guest: RegFormData) => async (dispatch: Function, getState: GetState) => {
    const state = getState()

    if (isDemoEvent(state) || isDemoMode(state.instance)) {
      return dispatch(navigateToThankYouMessageOld('yes'))
    }

    const response = await dispatch(
      callAPI(UPDATE_RSVP_OLD, eventId, extractFormData(guest), guest.response, getMemberRsvpId(state)),
    )

    if (!_.isEmpty(response.rsvp)) {
      dispatch(navigateToThankYouMessageOld(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
    }

    return response
  }

export const updateRsvp = createAsyncAction<wix.events.rsvp.UpdateRsvpResponse, {eventId: string; guest: RegFormData}>(
  'UPDATE_RSVP',
  async ({eventId, guest}, {getState, dispatch, extra: {serverApi}}) => {
    const state = getState()

    const response = await serverApi.updateRsvp(
      eventId,
      extractFormData(guest) as unknown as RegFormData,
      guest.response,
      getMemberRsvpId(state),
    )

    if (!_.isEmpty(response.rsvp)) {
      dispatch(navigateToThankYouMessage(RsvpStatus[guest.response].toLowerCase() as ThankYouMessageState))
    }

    return response
  },
)

export const updateRsvpStatusOld = (eventId: string, rsvpId: string, status: RsvpStatus) =>
  callAPI(UPDATE_RSVP_STATUS_OLD, eventId, rsvpId, status)

export const updateRsvpStatus = createAsyncAction<void, {eventId: string; rsvpId: string; status: RsvpStatus}>(
  'UPDATE_RSVP_STATUS',
  async ({eventId, rsvpId, status}, {extra: {serverApi}}) => {
    return serverApi.updateRsvpStatus(eventId, rsvpId, status)
  },
)

export const deleteRsvpOld = (eventId: string, rsvpId: string) => callAPI(DELETE_RSVP_OLD, eventId, rsvpId)

export const deleteRsvp = createAsyncAction<void, {eventId: string; rsvpId: string}>(
  'DELETE_RSVP',
  async ({eventId, rsvpId}, {extra: {serverApi}}) => {
    return serverApi.deleteRsvp(eventId, rsvpId)
  },
)

export const updateRsvpOrNavigateToForm = (
  state: State,
  dispatch: Function,
  resolve: Function,
  flowAPI: ControllerFlowAPI,
) => {
  const event = getEventSelector(state)
  const eventId = getEventId(event)
  const rsvpId = getMemberRsvpId(state)
  const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

  if (shouldNavigateToForm(state)) {
    useReduxToolkitEnabled ? dispatch(navigateToForm()) : dispatch(navigateToFormOld())
    return resolve(true)
  }

  useReduxToolkitEnabled
    ? dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.YES}))
    : dispatch(updateRsvpStatusOld(eventId, rsvpId, RsvpStatus.YES))
}

export const changeMemberRsvp =
  (returnFocusElement: string) =>
  (dispatch: Function, getState: GetState, {flowAPI}: StoreExtraArgs): Promise<boolean> => {
    return new Promise(resolve => {
      const state = getState()
      const event = getEventSelector(state)
      const eventTitle = getEventTitle(event)
      const eventId = getEventId(event)
      const params = {event: eventTitle, eventId, origin: BI_ORIGINS.EVENT_DETAILS}
      const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

      if (isEditor(state)) {
        return dispatch(
          openDialogModal({
            type: MembersModal.NO_CANCEL_RSVP,
            params,
            onClose: () => resolve(false),
            returnFocusElement,
          }),
        )
      }

      const noEnabled = isNoResponseEnabled(event)
      const status = getMemberEventRsvpStatus(state)
      const mode = getChangeRsvpModalMode(state)

      return dispatch(
        openDialogModal({
          type: MembersModal.UPDATE_RSVP,
          params: {...params, mode},
          onClose: () => resolve(false),
          returnFocusElement,
          onConfirm: async () => {
            const rsvpId = getMemberRsvpId(state)
            const useDetailsPageReduxToolkitEnabled = flowAPI.experiments.enabled(
              ExperimentNames.UseDetailsPageReduxToolkit,
            )

            if (!isMemberRsvpExists(state)) {
              useReduxToolkitEnabled ? dispatch(navigateToForm()) : dispatch(navigateToFormOld())

              return resolve(true)
            }

            if (noEnabled) {
              if ([RsvpStatus.YES, RsvpStatus.WAITING].includes(status)) {
                useReduxToolkitEnabled
                  ? await dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.NO}))
                  : await dispatch(updateRsvpStatusOld(eventId, rsvpId, RsvpStatus.NO))
              } else if (getMemberEventRsvpStatus(state) === RsvpStatus.NO) {
                await (isFull(event)
                  ? useReduxToolkitEnabled
                    ? dispatch(updateRsvpStatus({eventId, rsvpId, status: RsvpStatus.WAITING}))
                    : dispatch(updateRsvpStatusOld(eventId, rsvpId, RsvpStatus.WAITING))
                  : updateRsvpOrNavigateToForm(state, dispatch, resolve, flowAPI))
              }
            } else {
              useReduxToolkitEnabled
                ? await dispatch(deleteRsvp({eventId, rsvpId}))
                : await dispatch(deleteRsvpOld(eventId, rsvpId))
            }

            resolve(true)
            if (useDetailsPageReduxToolkitEnabled) {
              await dispatch(getEvent())
              dispatch(getMembers())
            } else {
              await dispatch(getEventOld())
              dispatch(getMembersOld())
            }
          },
        }),
      )
    })
  }

const getChangeRsvpModalMode = (state: State) => {
  const event = getEventSelector(state)
  const noEnabled = isNoResponseEnabled(event)
  const status = getMemberEventRsvpStatus(state)

  if (!isMemberRsvpExists(state)) {
    return ChangeRsvpModalMode.UPDATE_TO_YES
  }

  if (!noEnabled) {
    return ChangeRsvpModalMode.DELETE
  }

  if (isFull(event) && status === RsvpStatus.NO) {
    return ChangeRsvpModalMode.WAITLIST
  }

  if (status === RsvpStatus.NO) {
    return ChangeRsvpModalMode.UPDATE_TO_YES
  }

  return ChangeRsvpModalMode.UPDATE_TO_NO
}

export const handleRSVP = createAsyncAction<void>(
  'HANDLE_RSVP',
  async (_params, {getState, dispatch, extra: {flowAPI}}) => {
    const state = getState()
    const event = getEventSelector(state)
    const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    dispatch(registrationButtonClicked())

    if (await dispatch(ensureLoginForMembersOnly())) {
      if (isMemberRsvpExists(getState())) {
        if (isMemberAbleToFinishRsvp(getState())) {
          if (useReduxToolkitEnabled) {
            await dispatch(navigateToForm())
            return
          } else {
            return dispatch(navigateToFormOld())
          }
        } else {
          return
        }
      }
      if (flowAPI.environment.isMobile && isTicketed(event)) {
        if (useReduxToolkitEnabled) {
          await dispatch(navigateToTicketsPicker())
          return
        } else {
          return dispatch(navigateToTicketsPickerOld())
        }
      }

      useReduxToolkitEnabled ? dispatch(navigateToForm()) : dispatch(navigateToFormOld())
    }
  },
)

const setRsvpDetailsOld = (rsvpDetails: RegFormData) => ({
  type: SET_RSVP_DETAILS_OLD,
  payload: {rsvpDetails},
})

export const setRsvpDetails = createAction<RegFormData>('SET_RSVP_DETAILS')

const rsvpOld = (data?: any) => (dispatch: Function, getState: GetState) => {
  const state = getState()
  const rsvpDetails = data || getRsvpDetails(state)
  const event = getEventSelector(state)
  const eventId = getEventId(event)

  if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
    dispatch(updateRsvpOld(eventId, rsvpDetails))
  } else {
    dispatch(sendRsvpOld(eventId, rsvpDetails))
  }
}

const rsvp = createAsyncAction<void, RegFormData | void>('RSVP', async (data = undefined, {getState, dispatch}) => {
  const state = getState()
  const rsvpDetails = data || getRsvpDetails(state)
  const event = getEventSelector(state)
  const eventId = getEventId(event)

  if (isDemoEvent(state) || isDemoMode(state.instance)) {
    navigateToThankYouInDemoMode(dispatch, state, rsvpDetails.response)
    return
  }

  if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
    dispatch(updateRsvp({eventId, guest: rsvpDetails}))
  } else {
    dispatch(sendRsvp({eventId, guest: rsvpDetails}))
  }
})

const navigateToThankYouInDemoMode = (dispatch: Function, state: State, guestResponse: RsvpStatus) => {
  if (isMemberRsvpExists(state) && shouldNavigateToForm(state)) {
    dispatch(navigateToThankYouMessage('yes'))
  } else {
    const thankYouMessageState: ThankYouMessageState =
      guestResponse === RsvpStatus.YES ? 'yes' : guestResponse === RsvpStatus.WAITING ? 'waiting' : 'no'

    dispatch(navigateToThankYouMessage(thankYouMessageState))
  }
}

export const submitRsvpStep =
  (data: any) =>
  async (dispatch: Function, getState: GetState, {flowAPI}: StoreExtraArgs) => {
    const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    const state = getState()
    const currentStep = getCurrentStep(state)

    switch (currentStep) {
      case FormStep.BuyerDetails: {
        if (hasPolicies(state)) {
          useReduxToolkitEnabled ? dispatch(setRsvpDetails(data)) : dispatch(setRsvpDetailsOld(data))
        } else {
          useReduxToolkitEnabled ? dispatch(rsvp(data)) : dispatch(rsvpOld(data))
        }
        break
      }
      case FormStep.Policies: {
        useReduxToolkitEnabled ? await dispatch(rsvp()) : await dispatch(rsvpOld())
        break
      }
      default:
        break
    }

    dispatch(handleNextRsvpStep())
  }

export const editRsvpStepOld = (currentStep: FormStep) => ({
  type: EDIT_RSVP_STEP_OLD,
  payload: {
    currentStep,
  },
})

export const editRsvpStep = createAction<FormStep>('EDIT_RSVP_STEP')

export const handleNextRsvpStep =
  () =>
  (dispatch: Function, getState: GetState, {flowAPI}: StoreExtraArgs) => {
    const state = getState()
    const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    if (
      isStepCompleted({
        step: getCurrentStep(state),
        rsvpDetails: getRsvpDetails(state),
        agreedWithPolicies: hasAgreedWithPolicies(state),
      })
    ) {
      useReduxToolkitEnabled ? dispatch(nextStep()) : dispatch(nextStepOld())
    }
  }

const nextStepOld = () => (dispatch: Function, getState: GetState) => {
  const state = getState()
  const {rsvpDetails} = state.rsvp
  const next = RSVP_STEPS.find(step => {
    const isVisible = isStepVisible({step, hasPolicies: hasPolicies(state)})
    const isCompleted = isStepCompleted({
      step,
      rsvpDetails,
      agreedWithPolicies: hasAgreedWithPolicies(state),
    })
    return isVisible && !isCompleted
  })
  const last = RSVP_STEPS[RSVP_STEPS.length - 1]

  dispatch({
    type: NEXT_RSVP_STEP_OLD,
    payload: {
      nextStep: next ?? last,
    },
  })
}

export const nextStep = createAsyncAction<{nextStep: FormStep}, void>('NEXT_RSVP_STEP', async (_params, {getState}) => {
  const state = getState()
  const {rsvpDetails} = state.rsvp

  const next = RSVP_STEPS.find(step => {
    const isVisible = isStepVisible({step, hasPolicies: hasPolicies(state)})
    const isCompleted = isStepCompleted({
      step,
      rsvpDetails,
      agreedWithPolicies: hasAgreedWithPolicies(state),
    })

    return isVisible && !isCompleted
  })

  const last = RSVP_STEPS[RSVP_STEPS.length - 1]

  return {
    nextStep: next ?? last,
  }
})

export const changeResponseOld = (response: wix.events.rsvp.RsvpStatus) => ({
  type: CHANGE_RESPONSE_OLD,
  payload: {response},
})

export const changeResponse = createAction<wix.events.rsvp.RsvpStatus>('CHANGE_RESPONSE')
